import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNotification } from '../../contexts/NotificationContext';
import { contentService, categoryService } from '../../services/api';
import { CategorySimple } from '../../types';

// Componentes das abas
import GeneralSettings from '../../components/admin/settings/GeneralSettings';
import AppearanceSettings from '../../components/admin/settings/AppearanceSettings';
import ContactSettings from '../../components/admin/settings/ContactSettings';
import WhatsAppSettings from '../../components/admin/settings/WhatsAppSettings';
import WatermarkSettings from '../../components/admin/settings/WatermarkSettings';
import IntegrationsSettings from '../../components/admin/settings/IntegrationsSettings';
import GoogleBusinessSettings from '../../components/admin/settings/GoogleBusinessSettings';
import SEOSettings, { invalidTrackingFields } from '../../components/admin/settings/SEOSettings';
import HomepageSettings from '../../components/admin/settings/HomepageSettings';
import AboutSettings from '../../components/admin/settings/AboutSettings';

interface SiteSettings {
  // Dados gerais
  company_name: string;
  company_description: string;
  total_properties: number;
  active_cities: number;
  happy_clients: number;
  years_experience: number;
  show_stats_section: boolean;
  use_real_stats: boolean;
  
  // Aparência
  active_template: string;
  primary_color: string;
  secondary_color: string;
  logo: string;
  hero_title: string;
  hero_subtitle: string;
  hero_image: string;
  featured_categories: number[];
  
  // Contato
  phone: string;
  email: string;
  address: string;
  
  // WhatsApp
  whatsapp_number: string;
  whatsapp_message: string;
  whatsapp_property_message: string;
  whatsapp_lead_message: string;
  whatsapp_enabled: boolean;
  
  // Marca d'água
  watermark_enabled: boolean;
  watermark_logo_path: string;
  watermark_position: string;
  watermark_opacity: number;
  watermark_size: number;
  watermark_margin: number;
  
  // SEO
  site_title: string;
  site_description: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
}

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 2px solid #eee;
  margin-bottom: 2rem;
  overflow-x: auto;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 1rem 1.5rem;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  color: ${({ $active, theme }) => $active ? theme.colors.primary : '#666'};
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: #f8f9fa;
  }
`;

const TabContent = styled.div`
  min-height: 500px;
`;

const SaveButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: all 0.3s ease;
  z-index: 1000;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0,0,0,0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const tabs = [
  { id: 'general', label: 'Geral', icon: '🏢' },
  { id: 'appearance', label: 'Aparência', icon: '🎨' },
  { id: 'homepage', label: 'Página Inicial', icon: '🏠' },
  { id: 'about', label: 'Sobre Nós', icon: 'ℹ️' },
  { id: 'contact', label: 'Contato', icon: '📞' },
  { id: 'whatsapp', label: 'WhatsApp', icon: '💬' },
  { id: 'watermark', label: 'Marca d\'água', icon: '🏷️' },
  { id: 'integrations', label: 'Integrações', icon: '🔗' },
  { id: 'google-business', label: 'Google Business', icon: '🌟' },
  { id: 'seo', label: 'SEO', icon: '🔍' },
];

const AdminSettingsNew: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [categories, setCategories] = useState<CategorySimple[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    loadSettings();
    loadCategories();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSettings = async () => {
    try {
      const response = await contentService.getSiteSettings();
      setSettings(response);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      showNotification('Erro ao carregar configurações', 'error');
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoryService.getAll();
      setCategories(response);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const updateSettings = (updates: Partial<SiteSettings>) => {
    if (settings) {
      setSettings({ ...settings, ...updates });
      setHasChanges(true);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    const invalid = invalidTrackingFields(settings);
    if (invalid.length > 0) {
      setActiveTab('seo');
      showNotification(`Corrija o formato em SEO > Rastreamento: ${invalid.join(', ')}`, 'error');
      return;
    }

    try {
      setIsLoading(true);
      await contentService.updateSiteSettings(settings);
      setHasChanges(false);
      showNotification('Configurações salvas com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      showNotification('Erro ao salvar configurações', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const renderTabContent = () => {
    if (!settings) return <div>Carregando...</div>;

    switch (activeTab) {
      case 'general':
        return (
          <GeneralSettings
            settings={settings}
            onUpdate={updateSettings}
          />
        );
      case 'appearance':
        return (
          <AppearanceSettings
            settings={settings}
            categories={categories}
            onUpdate={updateSettings}
          />
        );
      case 'homepage':
        return (
          <HomepageSettings />
        );
      case 'about':
        return (
          <AboutSettings />
        );
      case 'contact':
        return (
          <ContactSettings />
        );
      case 'whatsapp':
        return (
          <WhatsAppSettings
            settings={settings}
            onUpdate={updateSettings}
          />
        );
      case 'watermark':
        return (
          <WatermarkSettings
            settings={settings}
            onUpdate={updateSettings}
          />
        );
      case 'integrations':
        return (
          <IntegrationsSettings />
        );
      case 'google-business':
        return (
          <GoogleBusinessSettings />
        );
      case 'seo':
        return (
          <SEOSettings
            settings={settings}
            onUpdate={updateSettings}
          />
        );
      default:
        return <div>Aba não encontrada</div>;
    }
  };

  return (
    <Container>
      <Header>
        <Title>Configurações do Sistema</Title>
        <Subtitle>Gerencie todas as configurações da sua imobiliária em um só lugar</Subtitle>
      </Header>

      <TabsContainer>
        {tabs.map(tab => (
          <Tab
            key={tab.id}
            $active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon} {tab.label}
          </Tab>
        ))}
      </TabsContainer>

      <TabContent>
        {renderTabContent()}
      </TabContent>

      {hasChanges && (
        <SaveButton onClick={handleSave} disabled={isLoading}>
          {isLoading ? '💾 Salvando...' : '💾 Salvar Alterações'}
        </SaveButton>
      )}
    </Container>
  );
};

export default AdminSettingsNew;
